import React, { useContext } from 'react'
import styled, { css, ThemeContext } from 'styled-components'
import { useTranslation } from 'react-i18next'

// Components
import Text from '../Text'
import Avatar, { AvatarSize } from '../Avatar'
import Icon from '../icon/Icon'
import AmountTag from '../tag/AmountTag'
import IconEmpty from '../icon/IconEmpty'

// Styles
import { mqFrom, mqTo } from '../../styles/mediaQueries'
import {
  fastTransition,
  gradientBackground,
  shadows,
} from '../../styles/effects'

// Types
import { ICompanyMember } from '../../common/models/Company'
import { RequestType } from '../../common/models/Request'

export interface IRequestProps {
  className?: string
  label: string | React.ReactNode
  title: string
  description?: string
  completed: boolean
  user?: ICompanyMember
  imgSrc?: string
  avatarInitials?: string
  memberRequest?: boolean
  ctaOnComplete?: string
  handleClick?: () => void
  // Days until the deadline, negative when late; left out when there is none
  date?: number
  type?: RequestType
  // The person the task is about, for the line that explains it
  targetName?: string
  badge?: React.ReactNode
  // Cypress handle, forwarded to the clickable card.
  'data-cy'?: string
}

interface IProps {
  className?: string
  title: string
  openRequests?: IRequestProps[]
}

export const renderCard = (request: IRequestProps, index: number) => (
  <Card
    key={`request-list-card-${request.title}-${index}`}
    data-cy={request['data-cy']}
    completed={request.completed}
    onClick={
      !request.completed || request.ctaOnComplete
        ? request.handleClick
        : undefined
    }
  >
    <StyledAvatar
      user={request.user}
      size={AvatarSize.ExtraSmall}
      imgSrc={
        !request.user && !request.imgSrc && !request.avatarInitials
          ? '/assets/images/logo.svg'
          : request.user?.picture || request.imgSrc
      }
      initials={request.avatarInitials}
      color={
        !request.user?.picture && !request.avatarInitials
          ? 'primary'
          : 'primaryLight'
      }
    />
    <TextWrapper>
      <StyledText marginBottom={4} textStyle="labelHeavy" color="textLight">
        {request.title}
      </StyledText>
      <StyledDate tag="div" color="textLighter" textStyle="caption">
        {request.label}
      </StyledDate>
    </TextWrapper>
    <Wrapper>
      {request.badge}
      {request.ctaOnComplete && (
        <CTAText textStyle="labelHeavy" color="textLighter">
          {request.ctaOnComplete}
        </CTAText>
      )}
      {(!request.completed || request.ctaOnComplete) && (
        <StyledIcon
          hideByDefault={!request.completed}
          size={12}
          type="ChevronRight"
        />
      )}
    </Wrapper>
  </Card>
)

const RequestListCard: React.FC<IProps> = ({
  className,
  openRequests,
  title,
}) => {
  const { t } = useTranslation()
  const theme = useContext(ThemeContext)

  return (
    <Container className={className}>
      <TitleRow>
        <Text textStyle="labelHeavy">{title}</Text>
        {openRequests && openRequests.length > 0 && (
          <StyledAmountTag
            size="medium"
            color="secondary"
            amount={openRequests.length}
          />
        )}
      </TitleRow>
      <ScrollContainer>
        {openRequests && openRequests.length > 0 ? (
          openRequests.map((request, index) => renderCard(request, index))
        ) : (
          <EmptyState>
            <Img
              secondColor={theme.colors.textLightest}
              color={theme.colors.textLighter}
              size={140}
            />
            <Text textStyle="caption" color="textLighter" textAlign="center">
              {t('requestList.nothingToSee')}
            </Text>
          </EmptyState>
        )}
      </ScrollContainer>
    </Container>
  )
}

export default RequestListCard

const Container = styled.div`
  width: 100%;
  box-sizing: border-box;
`

const ScrollContainer = styled.ul`
  box-sizing: border-box;
  // Room for the card shadows to render before the scroll box clips them.
  padding: 4px;
  margin: -4px;
  list-style: none;
  overflow-y: scroll;
  max-height: 592px;
  display: flex;
  flex-direction: column;
`

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  // Pinned to the text line-height so a taller badge doesn't push the margin
  // below it down, keeping this row's bottom edge level with "Quick actions".
  height: 16px;
  margin-bottom: 16px;
`

const StyledText = styled(Text)`
  transition: color ${fastTransition};
`

// Beside the chevron, it only joins on hover; touch screens have no hover to wait for
const CTAText = styled(Text)`
  display: none;
  transition: color ${fastTransition};

  ${mqTo.S`
    display: block;
  `}
`

const StyledDate = styled(Text)`
  transition: color ${fastTransition};

  ${mqTo.S`
    color: ${({ theme }) => theme.colors.text};
  `}
`

const StyledAmountTag = styled(AmountTag)`
  margin-left: 8px;
`

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 24px;
`

const Img = styled(IconEmpty)`
  width: 100%;
  margin: 16px 0;
  max-width: 168px;
`

const StyledIcon = styled(Icon)<{ hideByDefault?: boolean }>`
  display: ${({ hideByDefault }) => (hideByDefault ? 'none' : 'block')};

  > svg > path {
    fill: ${({ hideByDefault, theme }) =>
      hideByDefault ? theme.colors.primary : theme.colors.textLighter};
  }

  ${mqTo.S`
    display: block;
  `}
`

const Card = styled.li<{ onClick?: () => void; completed?: boolean }>`
  padding: 16px;
  background-color: ${({ theme }) => theme.colors.lightest};
  transition: background-color ${fastTransition}, border-color ${fastTransition};
  border: 1px solid
    ${({ completed, theme }) =>
      completed ? theme.colors.primaryLight : theme.colors.textLightest};
  display: flex;
  align-items: center;
  border-radius: 12px;
  margin-bottom: 12px;
  box-shadow: ${({ completed }) =>
    completed ? shadows.depthOne : shadows.depthTwo};

  ${({ onClick }) =>
    onClick &&
    css`
      cursor: pointer;

      &:hover {
        background: ${({ theme }) => gradientBackground(theme)};
        border-color: ${({ theme }) => theme.colors.primaryFadeLight};

        ${StyledText} {
          color: ${({ theme }) => theme.colors.primary};
        }

        ${CTAText} {
          display: block;
          color: ${({ theme }) => theme.colors.primary};
        }

        ${StyledDate} {
          color: ${({ theme }) => theme.colors.text};
        }

        ${StyledIcon} {
          display: block;

          > svg > path {
            fill: ${({ theme }) => theme.colors.primary};
          }
        }
      }
    `}
`

const StyledAvatar = styled(Avatar)`
  margin-right: 8px;
  flex-shrink: 0;

  ${mqFrom.M`
    margin-right: 12px;
  `}
`

const TextWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-left: auto;
  padding-left: 16px;
`
